import { ReactElement, useMemo, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { reject, reverse, sortBy, toNumber } from 'lodash-es'
import TuneIcon from '@mui/icons-material/Tune'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { Box, Divider, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { getGuidToMGroup } from '@editor/components/Controls/IFCUploadWidget/utils/ifcMetadataUtils'
import IFCFileSelect from '@editor/components/IFCFileSelect'
import { storeyToLabel } from '@editor/pages/Plans/components/AddPlanDialog/components/utils'
import { roofStoreyKey, useControlStore, useModelStore } from '@editorStores'
import { useActiveIfcFileGuid, useIfcModelMetadata } from '@editorHooks'
import { eventMatchesPointerModifier } from 'src/shortcuts/pointerModifiers'
import StoreyTypeToggleButton from '../StoreyTypeToggleButton'
import IFCFilterPopover from './IFCFilterPopover'
import { IFC_OTHER_TYPES } from './IFCOtherTypesFilter'
import IFCSettingsPopover from './IFCSettingsPopover'
import useIfcElementTypeSelection from './useIfcElementTypeSelection'

/** The four IFC element types with per-storey buttons (all others live in the dropdown). */
const IFC_MAIN_TYPES: { type: string; label: string }[] = [
  { type: 'inner_walls', label: 'IW' },
  { type: 'outer_walls', label: 'AW' },
  { type: 'slabs', label: 'D' },
  { type: 'roof_slabs', label: 'DA' },
]

const labelWidth = 76

/**
 * IFC file column of the storey controls, mirroring the planar-model storey controls: one row per
 * storey with IW/AW/D/DA visibility toggles (per storey, via the import storey assignments), an
 * "Alle SW" row toggling each type globally, and a dropdown for the remaining IFC types. Renders
 * nothing when the active IFC file has no elements.
 */
const IFCStoreyControls = (): ReactElement | null => {
  const theme = useTheme()
  const ifcPalette = theme.scenePalette.ifc as Record<string, string>
  const activeFileGuid = useActiveIfcFileGuid()
  const metadata = useIfcModelMetadata(activeFileGuid)
  const hasIfcElements = useMemo(
    () => Object.keys(getGuidToMGroup(metadata)).length > 0,
    [metadata],
  )

  const availableStoreys = useModelStore(state => state.availableStoreys)
  const orderedStoreys = useMemo(() => {
    const nonRoof = reverse(
      sortBy(
        reject(Array.from(availableStoreys), storey => storey === roofStoreyKey),
        storey => toNumber(storey),
      ),
    )
    return availableStoreys.has(roofStoreyKey) ? [roofStoreyKey, ...nonRoof] : nonRoof
  }, [availableStoreys])

  const glbTypeVisibility = useControlStore(state => state.glbTypeVisibility)
  const glbTypeVisibilityByStorey = useControlStore(state => state.glbTypeVisibilityByStorey)
  const glbHiddenStoreys = useControlStore(state => state.glbHiddenStoreys)
  const setGlbTypeVisibility = useControlStore(state => state.setGlbTypeVisibility)
  const setGlbTypeVisibilityForStorey = useControlStore(
    state => state.setGlbTypeVisibilityForStorey,
  )
  const setGlbStoreyHidden = useControlStore(state => state.setGlbStoreyHidden)

  const { selectIfcByType, selectIfcByTypeAndStorey } = useIfcElementTypeSelection()

  const visibleOtherTypesCount = IFC_OTHER_TYPES.filter(
    type => glbTypeVisibility[type] !== false,
  ).length
  const anyOtherTypeVisible = visibleOtherTypesCount > 0

  const showGltfModel = useControlStore(state => state.showGltfModel)
  const setShowGltfModel = useControlStore(state => state.setShowGltfModel)

  const showFilteredIfcElements = useControlStore(state => state.showFilteredIfcElements)
  const setShowFilteredIfcElements = useControlStore(state => state.setShowFilteredIfcElements)
  const filteredElementCount = useMemo(
    () => (metadata?.filteredElements ?? []).reduce((sum, group) => sum + group.length, 0),
    [metadata?.filteredElements],
  )

  const [settingsAnchorEl, setSettingsAnchorEl] = useState<HTMLElement | null>(null)
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null)

  // Toggle IFC/GLB model visibility (mirrors the former "IFC anzeigen" switch).
  useHotkeys('shift+y', () => setShowGltfModel(!showGltfModel), [showGltfModel, setShowGltfModel])

  if (!hasIfcElements || orderedStoreys.length === 0) return null

  return (
    <Stack direction="column" sx={{ minWidth: 0 }}>
      <Stack direction="row" alignItems="center" spacing={0.25} sx={{ px: 1, py: 0 }}>
        {showGltfModel ? (
          <IFCFileSelect hideLabel standardVariant compact />
        ) : (
          <Box pt={0.75}>
            <Typography fontSize="small">IFC</Typography>
          </Box>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Stack pt={0.5} spacing={0.5} direction="row">
          <Tooltip title="Einstellungen für IFC Modell">
            <IconButton
              size="small"
              aria-label="Einstellungen für IFC Modell"
              sx={{ p: 0, color: theme.palette.grey[700] }}
              onClick={event => setSettingsAnchorEl(event.currentTarget)}
              data-cy="btn-ifc-settings"
            >
              <TuneIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={showGltfModel ? 'IFC Modell verstecken' : ''}>
            <IconButton
              size="small"
              aria-label={showGltfModel ? 'IFC Modell verstecken' : undefined}
              sx={{ p: 0, color: theme.palette.grey[700] }}
              onClick={() => setShowGltfModel(!showGltfModel)}
              data-cy="btn-ifc-show-toggle"
            >
              {showGltfModel ? (
                <VisibilityIcon fontSize="small" />
              ) : (
                <VisibilityOffIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      <IFCFilterPopover anchorEl={filterAnchorEl} onClose={() => setFilterAnchorEl(null)} />
      <IFCSettingsPopover anchorEl={settingsAnchorEl} onClose={() => setSettingsAnchorEl(null)} />

      {/* The per-storey and "Alle SW" toggles only make sense while the IFC model is shown, so the
          eye toggle in the header collapses everything below it down to just the header bar. */}
      {showGltfModel && (
        <>
          <Stack direction="column" spacing={0.25} sx={{ px: 1, py: 0.5 }}>
            {orderedStoreys.map(storey => {
              const storeyHidden = glbHiddenStoreys[storey] === true
              return (
                <Stack key={storey} direction="row" spacing={0.5} alignItems="center">
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ width: labelWidth, flexShrink: 0 }}
                  >
                    <IconButton
                      size="small"
                      sx={{ p: 0, color: theme.palette.grey[700] }}
                      onClick={() => setGlbStoreyHidden(storey, !storeyHidden)}
                      data-cy={`btn-ifc-${storey}-visibility`}
                    >
                      {storeyHidden ? (
                        <VisibilityOffIcon fontSize="small" />
                      ) : (
                        <VisibilityIcon fontSize="small" />
                      )}
                    </IconButton>
                    <Typography fontSize="small" noWrap>
                      {storeyToLabel(storey)}
                    </Typography>
                  </Stack>
                  {IFC_MAIN_TYPES.map(({ type, label }) => {
                    const visible = glbTypeVisibilityByStorey[storey]?.[type] !== false
                    return (
                      <StoreyTypeToggleButton
                        key={type}
                        value={`ifc-${storey}-${type}`}
                        label={label}
                        color={ifcPalette[type]}
                        selected={visible}
                        onToggle={() => setGlbTypeVisibilityForStorey(storey, type, !visible)}
                        onSelect={event =>
                          selectIfcByTypeAndStorey(type, storey, {
                            extend: eventMatchesPointerModifier(event, 'multiSelect'),
                          })
                        }
                        dataCy={`btn-ifc-${storey}-${type}-visibility`}
                      />
                    )
                  })}
                </Stack>
              )
            })}
          </Stack>

          <Divider />

          <Stack direction="column" spacing={0.25} sx={{ px: 1, py: 0.5 }}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography fontSize="small" noWrap sx={{ width: labelWidth, flexShrink: 0 }}>
                Alle SW
              </Typography>
              {IFC_MAIN_TYPES.map(({ type, label }) => {
                const visible = glbTypeVisibility[type] !== false
                return (
                  <StoreyTypeToggleButton
                    key={type}
                    value={`ifc-all-${type}`}
                    label={label}
                    color={ifcPalette[type]}
                    selected={visible}
                    onToggle={() => setGlbTypeVisibility(type, !visible)}
                    onSelect={event =>
                      selectIfcByType(type, {
                        extend: eventMatchesPointerModifier(event, 'multiSelect'),
                      })
                    }
                    dataCy={`btn-ifc-all-${type}-visibility`}
                  />
                )
              })}
            </Stack>

            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: labelWidth, flexShrink: 0 }} />
              <StoreyTypeToggleButton
                value="ifc-other-types"
                label={`Sonstiges (${visibleOtherTypesCount})`}
                selected={anyOtherTypeVisible}
                onToggle={() =>
                  IFC_OTHER_TYPES.forEach(type => setGlbTypeVisibility(type, !anyOtherTypeVisible))
                }
                onClickSettings={event => setFilterAnchorEl(event.currentTarget)}
                settingsIcon="filter"
                dataCy="btn-ifc-other-types-visibility"
                settingsDataCy="btn-ifc-other-types-filter"
              />
              {filteredElementCount > 0 && (
                <StoreyTypeToggleButton
                  value="ifc-filtered-elements"
                  label={`Gefiltert (${filteredElementCount})`}
                  selected={showFilteredIfcElements}
                  onToggle={() => setShowFilteredIfcElements(!showFilteredIfcElements)}
                  dataCy="btn-ifc-filtered-elements-visibility"
                />
              )}
            </Stack>
          </Stack>
        </>
      )}
    </Stack>
  )
}

export default IFCStoreyControls
